import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import MosSmartDialog from '../MosSmartDialog'
import createDialogTitle from '../../../../generic/utilities/dialogUtil'
import FabricationDocumentsPanel, { DIALOG_PANES_HEIGHT } from './FabricationDocumentsPanel'

// Historia 148 — el modal de documentación de UNA fabricación. Su cuerpo (lista, visor, acciones y
// alta) vive en `FabricationDocumentsPanel` desde la historia 160, que lo monta también como pestaña
// del detalle del job; aquí queda el diálogo y la regla de cuándo cargar.
//
// ⚠️ EL MODAL NECESITA ALTURA PROPIA. `SmartDocumentViewer` y sus visores 2D/3D pintan con
// `height: '100%'`: montados en un contenedor de altura automática resuelven a CERO píxeles y el
// fichero se descarga pero no se ve. Por eso el diálogo va con `growContainerHeight` y
// `paperHeight`, igual que hace `Document.jsx` al abrir su carrusel.
//
// ⚠️ S14/RN33 — EL MODAL ABIERTO NO SE REFRESCA SOLO. MOS sondea su contexto activo cada pocos
// segundos: si la carga dependiera de los ids que llegan por props, cada tick del sondeo repintaría
// la lista bajo el dedo del operario. Por eso los ids se CONGELAN al abrir (`request`) y el panel
// carga con esa petición congelada, no con las props. Reabrir incrementa su `token`, que es justo lo
// que RN33 pide: reapertura = recarga de la selección vigente.

const NO_IDS = { fabricationOrderId: '', jobReferenceId: '' }
// `token` 0 = todavía no se ha abierto nunca. Cada apertura lo incrementa, así que reabrir con la
// MISMA selección también recarga (RN33/S3).
const NO_REQUEST = { token: 0, ...NO_IDS }

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {string|number} props.fabricationOrderId
 * @param {string|number} props.jobReferenceId `jobReference.id`, nunca el id del nodo de workflow
 * @param {Function} props.onClose
 */
const FabricationDocumentsDialog = ({ open = false, fabricationOrderId = '', jobReferenceId = '', onClose = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const [request, setRequest] = useState(NO_REQUEST)

  // Los ids vigentes viven en una ref: el sondeo de MOS los actualiza sin disparar ninguna carga.
  const latestIdsRef = useRef(NO_IDS)
  latestIdsRef.current = { fabricationOrderId, jobReferenceId }

  useEffect(() => {
    if (!open) {
      return
    }
    // Congela los ids VIGENTES en el instante de abrir.
    setRequest((previous) => ({ token: previous.token + 1, ...latestIdsRef.current }))
  }, [open])

  return (
    <MosSmartDialog
      setOpen={open}
      accept={false}
      cancel={false}
      close
      closeCallback={onClose}
      title={createDialogTitle({ color: 'primary', title: t_('Documents'), icon: null })}
      minWidth='92vw'
      paperWidth='92vw'
      maxPaperWidth='92vw'
      paperHeight='85vh'
      maxPaperHeight='85vh'
      growContainerHeight
      // ⚠️ El scroll tiene que ser de CADA PANEL, no del dialogo. `DialogContent` trae el suyo por
      // `scroll='paper'`, y entre el y el contenido `SmartDialog` interpone dos `Grid`: sin
      // `min-height: 0` en esa cadena, un hijo flex no baja de su contenido, la lista empuja la
      // altura y aparece una unica barra que arrastra las dos columnas a la vez.
      contentSx={{
        overflow: 'hidden',
        minHeight: 0,
        '& > .MuiGrid-container': { minHeight: 0 },
        '& > .MuiGrid-container > .MuiGrid-item': { minHeight: 0 }
      }}
    >
      <FabricationDocumentsPanel
        fabricationOrderId={request.fabricationOrderId}
        jobReferenceId={request.jobReferenceId}
        requestToken={request.token}
        height={DIALOG_PANES_HEIGHT}
      />
    </MosSmartDialog>
  )
}

FabricationDocumentsDialog.propTypes = {
  open: PropTypes.bool,
  fabricationOrderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  jobReferenceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func
}

export default FabricationDocumentsDialog
